import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Mail,
  ShieldCheck,
  Users,
  DollarSign,
  QrCode,
} from 'lucide-react';

import {
  PAGE_BG,
  INPUT,
  LABEL,
  useBrandBodyBackground,
} from '../../referral/brandTheme';
import PublicLayout from '../../layouts/PublicLayout';
import { usePortalManifest } from '../PortalLayout';
import { usePortal } from '../../../contexts/PortalContext';
import {
  useRequestPortalCode,
  useVerifyPortalCode,
} from '../../../services/portal';

// Passwordless portal login: email -> 6-digit code -> in. The request-code
// response is identical whether or not the email exists (zero enumeration), so
// the UI always advances to the code step with the same copy.
const PortalLogin = () => {
  useBrandBodyBackground();
  // Same manifest/theme-color as the portal proper: installing from the login
  // screen must install the PORTAL, and mobile browser chrome should match.
  usePortalManifest();
  const navigate = useNavigate();
  const { portalToken, portalSignIn } = usePortal();

  const [step, setStep] = useState('email'); // email | code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);
  const codeInputRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);
  useEffect(() => {
    if (portalToken) navigate('/portal', { replace: true });
  }, [portalToken, navigate]);
  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus();
  }, [step]);

  const startCooldown = (seconds) => {
    setCooldown(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const requestCode = useRequestPortalCode(
    (res) => {
      setErrorMsg(null);
      setStep('code');
      startCooldown(res.cooldownSeconds || 60);
    },
    {
      onError: (err) => {
        // Only the cooldown 429 carries a real message; everything else is generic.
        const msg =
          err.response?.data?.msg || 'Something went wrong. Please try again.';
        setErrorMsg(msg);
        if (err.response?.status === 429) {
          const remaining = parseInt(
            String(msg).match(/(\d+) seconds/)?.[1],
            10,
          );
          if (remaining > 0) startCooldown(remaining);
        }
      },
    },
  );

  const verifyCode = useVerifyPortalCode(
    (res) => {
      portalSignIn(res);
      navigate(res.status === 'active' ? '/portal' : '/portal/status', {
        replace: true,
      });
    },
    {
      onError: (err) => {
        setErrorMsg(err.response?.data?.msg || 'Invalid or expired code.');
        setCode('');
        codeInputRef.current?.focus();
      },
    },
  );

  const submitEmail = (e) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!email.trim()) return;
    requestCode.mutate(email.trim());
  };

  const submitCode = (e) => {
    e.preventDefault();
    setErrorMsg(null);
    if (code.trim().length !== 6) return;
    verifyCode.mutate({ email: email.trim(), code: code.trim() });
  };

  return (
    // Same shell as /referral (PublicLayout owns the black brand bar + footer);
    // headerBadge adds the "│ Partner Portal" lockup next to the logo.
    <PublicLayout
      background={PAGE_BG}
      footerClassName="text-neutral-300"
      footerVariant="dark"
      headerBadge="Partner Portal"
    >
      {/* Top-flow like /referral (no vertical centering) so the tile and the
          footer sit at the same heights across the brand pages. */}
      <div className="mx-auto flex w-full max-w-xl flex-col items-center py-6 text-white">
        <div className="w-full max-w-sm rounded-2xl bg-[#4A4A4E] p-6 shadow-lg ring-1 ring-white/10 sm:p-8">
          {step === 'email' ? (
            <form onSubmit={submitEmail} noValidate>
              <div className="mb-3 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/10">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <h1 className="text-lg font-bold text-white">
                  Welcome back, partner
                </h1>
                <p className="mt-1 text-sm text-neutral-300">
                  Sign in to see your referrals and rewards.
                </p>
              </div>
              <div className="mx-auto mb-4 h-px w-2/3 bg-white/10" />
              <label htmlFor="portal-email" className={LABEL}>
                Email address
              </label>
              <input
                id="portal-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                className={INPUT}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="mt-2 text-xs leading-snug text-neutral-400">
                We&apos;ll email you a one-time code — the address you used when
                you signed up as a referral partner.
              </p>
              {errorMsg ? (
                <p
                  role="alert"
                  className="mt-3 rounded-md bg-red-500/15 px-3 py-2 text-xs font-medium text-red-300"
                >
                  {errorMsg}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={requestCode.isLoading || !email.trim()}
                className="mt-5 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-white/10 text-sm font-medium text-white transition-colors hover:bg-neutral-200 hover:!text-black disabled:pointer-events-none disabled:opacity-50"
              >
                {requestCode.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Email me a code
              </button>
            </form>
          ) : (
            <form onSubmit={submitCode} noValidate>
              <div className="mb-1 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/10">
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
                <h1 className="text-lg font-bold text-white">
                  Check your email
                </h1>
              </div>
              <p className="mb-5 mt-1 text-center text-xs leading-snug text-neutral-400">
                If <span className="text-neutral-200">{email}</span> is
                registered with our partner program, a 6-digit code is on its
                way. It expires in 10 minutes.
              </p>
              <label htmlFor="portal-code" className={LABEL}>
                6-digit code
              </label>
              <input
                id="portal-code"
                ref={codeInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className={`${INPUT} text-center text-xl tracking-[0.5em]`}
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
              {errorMsg ? (
                <p
                  role="alert"
                  className="mt-3 rounded-md bg-red-500/15 px-3 py-2 text-xs font-medium text-red-300"
                >
                  {errorMsg}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={verifyCode.isLoading || code.length !== 6}
                className="mt-5 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-white/10 text-sm font-medium text-white transition-colors hover:bg-neutral-200 hover:!text-black disabled:pointer-events-none disabled:opacity-50"
              >
                {verifyCode.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Sign in
              </button>
              <div className="mt-4 flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setErrorMsg(null);
                    setCode('');
                  }}
                  className="min-h-[44px] font-medium text-neutral-400 transition-colors hover:text-white"
                >
                  Use a different email
                </button>
                <button
                  type="button"
                  disabled={cooldown > 0 || requestCode.isLoading}
                  onClick={() => requestCode.mutate(email.trim())}
                  className="min-h-[44px] font-medium text-neutral-400 transition-colors hover:text-white disabled:opacity-50"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* What's inside — a reminder of why the portal is worth logging into. */}
        <div className="mt-6 flex items-center justify-center gap-5 text-xs text-neutral-400 sm:gap-7">
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" /> Referral progress
          </span>
          <span className="inline-flex items-center gap-1.5">
            <DollarSign className="h-3.5 w-3.5" /> Rewards
          </span>
          <span className="inline-flex items-center gap-1.5">
            <QrCode className="h-3.5 w-3.5" /> Your QR code
          </span>
        </div>

        <p className="mt-6 max-w-sm text-center text-xs leading-snug text-neutral-400">
          Not a partner yet? Become one at our{' '}
          <a
            href="/partner-signup"
            className="text-neutral-300 underline hover:text-white"
          >
            partner sign-up
          </a>
          .
        </p>
      </div>
    </PublicLayout>
  );
};

export default PortalLogin;
